import type {
  ActionProposal,
  AssistantBackend,
  Attachment,
  AnswerAction,
  AnswerSource,
  OutgoingPayload,
  Quota,
  SessionState,
  StreamProtocol,
  TraceStep,
  TransportChunk,
} from "../types.ts";
import { joinUrl } from "./util.ts";
import { normalizeQonnexAgentEvent } from "./qonnex-agent-stream.ts";

export interface TransportInit {
  apiBase: string;
  chatPath: string;
  sessionPath: string;
  headers: Record<string, string>;
  credentials: RequestCredentials;
  timeout: number;
  getToken: (() => string | null | Promise<string | null>) | null;
  streamProtocol?: StreamProtocol;
  backend?: AssistantBackend | string | null;
}

export class TransportError extends Error {
  status: number | undefined;
  code: string | undefined;
  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = "TransportError";
    this.status = status;
    this.code = code;
  }
  /** 401/403 — app chủ cần làm mới JWT rồi gọi retry(). (403 vì mã khách bị tắt KHÔNG phải lỗi JWT.) */
  get isAuth(): boolean {
    return (this.status === 401 || this.status === 403) && !this.isClientGate && this.partyGate === null;
  }
  /** 403 `no_party_membership` | `active_party_mismatch` (ami-agent-v1 1.1.0-draft, ADR-0001 §4): a NEW JWT does not fix it — the person's
   *  party is wrong, so it is NOT an auth error (no `auth:error`, no refresh loop); the widget says which of the two it is. */
  get partyGate(): "no_party_membership" | "active_party_mismatch" | null {
    return this.status === 403 && (this.code === "no_party_membership" || this.code === "active_party_mismatch") ? this.code : null;
  }
  /** Server từ chối theo mã khách (`client_disabled`, `client_required`): thông điệp server đọc được cho người dùng. */
  get isClientGate(): boolean {
    return this.status === 403 && typeof this.code === "string" && this.code.startsWith("client_");
  }
}

/* Không dùng EventSource: nó chỉ biết GET và không gắn được header, mà mọi
   request ở đây đều phải mang Authorization. Nên đọc SSE thủ công từ body của
   một POST. */
export class Transport {
  private init: TransportInit;

  constructor(init: TransportInit) {
    this.init = init;
  }

  update(patch: Partial<TransportInit>): void {
    Object.assign(this.init, patch);
  }

  /** Backend cục bộ, nếu có. Tên chuỗi được tra lúc gọi chứ không lúc dựng,
   *  để file dữ liệu demo nạp sau widget vẫn được nhận. */
  private local(): AssistantBackend | null {
    const b = this.init.backend;
    if (!b) return null;
    if (typeof b === "string") {
      const found = (globalThis as unknown as Record<string, unknown>)[b];
      if (!found || typeof (found as AssistantBackend).chat !== "function") {
        throw new TransportError('Không tìm thấy backend "' + b + '"', 503, "backend_missing");
      }
      return found as AssistantBackend;
    }
    return b;
  }

  private async auth(): Promise<{ token: string | null }> {
    return { token: this.init.getToken ? ((await this.init.getToken()) ?? null) : null };
  }

  /** Gọi getToken() TRƯỚC MỖI request, không cache: token hết hạn giữa phiên
   *  chat là chuyện bình thường, và app chủ là nơi biết cách làm mới. */
  private async headers(accept: string): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      Accept: accept,
      "Content-Type": "application/json",
      ...this.init.headers,
    };
    if (this.init.getToken) {
      const token = await this.init.getToken();
      if (token) headers.Authorization = "Bearer " + token;
    }
    return headers;
  }

  /** Timeout riêng, nối với signal của người gọi: huỷ vì hết giờ và huỷ vì
   *  người dùng bấm Dừng phải phân biệt được. */
  private withTimeout(signal: AbortSignal): { signal: AbortSignal; done: () => void; timedOut: () => boolean } {
    if (!this.init.timeout) return { signal, done: () => {}, timedOut: () => false };
    const controller = new AbortController();
    let expired = false;
    const timer = setTimeout(() => {
      expired = true;
      controller.abort();
    }, this.init.timeout);
    const forward = () => controller.abort();
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", forward, { once: true });
    return {
      signal: controller.signal,
      done: () => {
        clearTimeout(timer);
        signal.removeEventListener("abort", forward);
      },
      timedOut: () => expired,
    };
  }

  /** Tóm tắt + ba gợi ý + hạn mức, lấy một lần lúc mở panel. */
  async fetchSession(context: Record<string, unknown> | null, signal: AbortSignal, conversationId: string | null = null, fresh = false): Promise<SessionState> {
    const local = this.local();
    if (local) {
      const auth = await this.auth();
      return (await runLocal(() => local.session({ context, conversationId, fresh }, auth))) || {};
    }
    const url = joinUrl(this.init.apiBase, this.init.sessionPath);
    const guard = this.withTimeout(signal);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: await this.headers("application/json"),
        credentials: this.init.credentials,
        body: JSON.stringify({ context: context || undefined, conversationId: conversationId || undefined, fresh: fresh || undefined }),
        signal: guard.signal,
      });
      if (!response.ok) {
        const detail = await safeErrorBody(response);
        throw new TransportError(detail.message || "HTTP " + response.status, response.status, detail.code);
      }
      const body = (await response.json()) as Record<string, unknown>;
      const data = (body.data && typeof body.data === "object" ? body.data : body) as SessionState;
      return data || {};
    } finally {
      guard.done();
    }
  }

  /** Có server (apiBase) — các tính năng Hub chỉ chạy khi có server. */
  hasServer(): boolean {
    return !this.init.backend;
  }

  /** Gọi một endpoint JSON của Hub: /api/v1/assistant/hub/<path>, kèm Authorization như mọi request. */
  async api<T = Record<string, unknown>>(method: "GET" | "POST" | "PATCH" | "DELETE", path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    const hubPath = this.init.sessionPath.replace(/\/session\/?$/, "") + "/hub" + path;
    const guard = this.withTimeout(signal || new AbortController().signal);
    try {
      const response = await fetch(joinUrl(this.init.apiBase, hubPath), {
        method,
        headers: await this.headers("application/json"),
        credentials: this.init.credentials,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: guard.signal,
      });
      if (!response.ok) {
        const detail = await safeErrorBody(response);
        throw new TransportError(detail.message || "HTTP " + response.status, response.status, detail.code);
      }
      if (response.status === 204) return {} as T; // DELETE idempotent (vd. /hub/erp-links/{id}) ⛔ thân
      return (await response.json()) as T;
    } finally {
      guard.done();
    }
  }

  /** Lấy nội dung nhị phân từ Hub (`/hub/files/:id`) hoặc một URL tuyệt đối (bản xuất PDF), kèm Authorization. */
  async blob(pathOrUrl: string, signal?: AbortSignal): Promise<Blob> {
    const url = /^https?:\/\//i.test(pathOrUrl) ? pathOrUrl : joinUrl(this.init.apiBase, this.init.sessionPath.replace(/\/session\/?$/, "") + "/hub" + pathOrUrl);
    const headers = await this.headers("*/*");
    delete headers["Content-Type"];
    const guard = this.withTimeout(signal || new AbortController().signal);
    try {
      const response = await fetch(url, { headers, credentials: this.init.credentials, signal: guard.signal });
      if (!response.ok) {
        const detail = await safeErrorBody(response);
        throw new TransportError(detail.message || "HTTP " + response.status, response.status, detail.code);
      }
      return await response.blob();
    } finally {
      guard.done();
    }
  }

  /** Bản demo: xoá dữ liệu hỏi đáp thử của kho này (cần mật khẩu). */
  async demoReset(password: string): Promise<void> {
    const path = this.init.sessionPath.replace(/\/session\/?$/, "") + "/demo/reset";
    const response = await fetch(joinUrl(this.init.apiBase, path), { method: "POST", headers: await this.headers("application/json"), credentials: this.init.credentials, body: JSON.stringify({ password }) });
    if (!response.ok) {
      const detail = await safeErrorBody(response);
      throw new TransportError(detail.message || "HTTP " + response.status, response.status, detail.code);
    }
  }

  /** Giọng nói → chữ: gửi đoạn ghi âm (thân nhị phân) lên `…/transcribe`, nhận `{ text }`. */
  async transcribe(audio: Blob, signal?: AbortSignal): Promise<string> {
    const path = this.init.sessionPath.replace(/\/session\/?$/, "") + "/transcribe";
    const headers = await this.headers("application/json");
    headers["Content-Type"] = audio.type || "audio/webm";
    const guard = this.withTimeout(signal || new AbortController().signal);
    try {
      const response = await fetch(joinUrl(this.init.apiBase, path), { method: "POST", headers, credentials: this.init.credentials, body: audio, signal: guard.signal });
      if (!response.ok) {
        const detail = await safeErrorBody(response);
        throw new TransportError(detail.message || "HTTP " + response.status, response.status, detail.code);
      }
      return String(((await response.json()) as { text?: string }).text || "");
    } finally {
      guard.done();
    }
  }

  /** Tải một tệp lên Hub (thân nhị phân, tên trong header). Trả về thông tin tệp do server ghi nhận. */
  async upload(file: Blob, name: string): Promise<{ id: string; name: string; type: string; size: number }> {
    const hubPath = this.init.sessionPath.replace(/\/session\/?$/, "") + "/hub/files";
    const headers = await this.headers("application/json");
    headers["Content-Type"] = file.type || "application/octet-stream";
    headers["X-File-Name"] = encodeURIComponent(name);
    const response = await fetch(joinUrl(this.init.apiBase, hubPath), { method: "POST", headers, credentials: this.init.credentials, body: file });
    if (!response.ok) {
      const detail = await safeErrorBody(response);
      throw new TransportError(detail.message || "HTTP " + response.status, response.status, detail.code);
    }
    return ((await response.json()) as { file: { id: string; name: string; type: string; size: number } }).file;
  }

  async clearSession(signal: AbortSignal): Promise<void> {
    const local = this.local();
    if (local) {
      if (local.clear) await runLocal(async () => local.clear?.(await this.auth()));
      return;
    }
    const url = joinUrl(this.init.apiBase, this.init.sessionPath);
    const guard = this.withTimeout(signal);
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: await this.headers("application/json"),
        credentials: this.init.credentials,
        signal: guard.signal,
      });
      if (!response.ok) {
        const detail = await safeErrorBody(response);
        throw new TransportError(detail.message || "HTTP " + response.status, response.status, detail.code);
      }
    } finally {
      guard.done();
    }
  }

  /**
   * Gửi một lượt hỏi. `wantStream` chỉ là mong muốn: nếu server trả
   * `application/json` thì đọc như JSON, không cần thoả thuận trước.
   */
  async send(
    payload: OutgoingPayload,
    signal: AbortSignal,
    emit: (chunk: TransportChunk) => void,
    wantStream: boolean,
  ): Promise<void> {
    const local = this.local();
    if (local) {
      const guard = this.withTimeout(signal);
      try {
        const auth = await this.auth();
        await runLocal(() => local.chat(payload, auth, emit, guard.signal));
      } catch (error) {
        if (guard.timedOut()) throw new TransportError("timeout");
        throw error;
      } finally {
        guard.done();
      }
      emit({ type: "done" });
      return;
    }
    const url = joinUrl(this.init.apiBase, this.init.chatPath);
    const accept = wantStream ? "text/event-stream, application/json" : "application/json";
    const headers = await this.headers(accept);
    const guard = this.withTimeout(signal);

    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers,
        credentials: this.init.credentials,
        body: JSON.stringify(payload),
        signal: guard.signal,
      });
    } catch (error) {
      guard.done();
      if (guard.timedOut()) throw new TransportError("timeout");
      throw error;
    }

    try {
      if (!response.ok) {
        const detail = await safeErrorBody(response);
        /* Hạn mức, id hội thoại và bước tiếp theo vẫn có giá trị dù request
           hỏng — đọc chúng ra trước khi ném. */
        if (detail.raw) emitEnvelope(detail.raw, emit);
        throw new TransportError(detail.message || "HTTP " + response.status, response.status, detail.code);
      }

      const contentType = (response.headers.get("content-type") || "").toLowerCase();
      if (contentType.includes("text/event-stream") && response.body) await this.readSse(response, emit);
      else await this.readJson(response, emit);
    } finally {
      guard.done();
    }
  }

  private async readJson(response: Response, emit: (chunk: TransportChunk) => void): Promise<void> {
    const body = (await response.json()) as Record<string, unknown>;
    const data = (body.data && typeof body.data === "object" ? body.data : body) as Record<string, unknown>;

    emitEnvelope(data, emit);

    const text = pickString(data, ["reply", "answer", "message", "text", "content", "output"]);
    if (text != null) emit({ type: "message", text, messageId: pickString(data, ["messageId", "id"]) });
    else emit({ type: "error", message: "Phản hồi không có nội dung." });

    emit({ type: "done" });
  }

  private async readSse(response: Response, emit: (chunk: TransportChunk) => void): Promise<void> {
    const reader = (response.body as ReadableStream<Uint8Array>).getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let finished = false;

    try {
      outer: for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        /* Một sự kiện SSE kết thúc bằng dòng trống. Chấp nhận cả \n\n lẫn
           \r\n\r\n vì proxy có thể chuẩn hoá xuống dòng kiểu Windows. */
        for (;;) {
          const found = nextBoundary(buffer);
          if (!found) break;
          const block = buffer.slice(0, found.index);
          buffer = buffer.slice(found.index + found.length);
          if (handleSseBlock(block, emit, this.init.streamProtocol)) {
            finished = true;
            break outer;
          }
        }
      }
      /* Server đóng kết nối mà không để lại dòng trống cuối — phần đuôi vẫn
         là một sự kiện hợp lệ. */
      if (!finished) {
        buffer += decoder.decode();
        if (buffer.trim()) handleSseBlock(buffer, emit, this.init.streamProtocol);
      }
    } finally {
      try {
        await reader.cancel();
      } catch {
        /* stream đã đóng */
      }
    }

    emit({ type: "done" });
  }
}

interface Boundary {
  index: number;
  length: number;
}

function nextBoundary(buffer: string): Boundary | null {
  const lf = buffer.indexOf("\n\n");
  const crlf = buffer.indexOf("\r\n\r\n");
  if (lf < 0 && crlf < 0) return null;
  if (crlf >= 0 && (lf < 0 || crlf < lf)) return { index: crlf, length: 4 };
  return { index: lf, length: 2 };
}

/** Các trường đi kèm mà cả SSE lẫn JSON đều có thể mang: id hội thoại, nguồn,
 *  hành động, hạn mức, cờ số mẫu. */
function emitEnvelope(obj: Record<string, unknown>, emit: (chunk: TransportChunk) => void): void {
  const conversationId = pickString(obj, ["conversationId", "conversation_id", "sessionId", "threadId", "thread_id"]);
  if (conversationId) emit({ type: "conversation", conversationId });

  const sources = normalizeSources(obj.sources ?? obj.citations);
  if (sources.length) emit({ type: "sources", sources });

  const actions = normalizeActions(obj.actions ?? obj.nextSteps ?? obj.next_steps);
  if (actions.length) emit({ type: "actions", actions });

  const quota = normalizeQuota(obj.quota ?? obj.usage);
  if (quota) emit({ type: "quota", quota });

  const sample = obj.sampleData ?? obj.sample_data ?? obj.isSample;
  if (typeof sample === "boolean") emit({ type: "sample", sampleData: sample });

  const proposals = normalizeProposals(obj.proposals);
  if (proposals.length) emit({ type: "proposals", proposals });

  const files = normalizeFiles(obj.files);
  if (files.length) emit({ type: "files", files });

  const trace = normalizeTrace(obj.trace);
  if (trace.length) emit({ type: "trace", trace });

  const intent = obj.intent as Record<string, unknown> | undefined;
  if (intent && typeof intent.kind === "string" && typeof intent.label === "string") {
    emit({ type: "intent", intent: { kind: intent.kind.slice(0, 20), label: intent.label.slice(0, 60) } });
  }

  const prompts = obj.suggestions ?? obj.prompts ?? obj.followUps;
  if (Array.isArray(prompts)) {
    const list = prompts.filter((p): p is string => typeof p === "string");
    if (list.length) emit({ type: "prompts", prompts: list });
  }
}

/** Trả về true nếu server báo kết thúc. */
function handleSseBlock(
  block: string,
  emit: (chunk: TransportChunk) => void,
  streamProtocol: StreamProtocol = "assistant-v1",
): boolean {
  let event = "message";
  const dataLines: string[] = [];

  for (const line of block.split(/\r?\n/)) {
    if (!line || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value.trim();
    else if (field === "data") dataLines.push(value);
  }

  if (!dataLines.length) return false;
  const data = dataLines.join("\n");

  /* Quy ước OpenAI-style: dòng [DONE] kết thúc stream. */
  if (data === "[DONE]") return true;
  if (streamProtocol !== "qonnex-agent" && (event === "done" || event === "end")) return true;

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    /* Server chỉ đẩy chữ trần — vẫn coi là một mẩu delta. */
    emit({ type: "delta", text: data });
    return false;
  }

  if (typeof parsed === "string") {
    emit({ type: "delta", text: parsed });
    return false;
  }
  if (!parsed || typeof parsed !== "object") return false;
  const obj = parsed as Record<string, unknown>;

  if (event === "error" || obj.error) {
    const message = pickString(obj, ["message", "error", "detail"]) ?? "Trợ lý gặp sự cố khi trả lời.";
    emit({ type: "error", message, code: pickString(obj, ["code"]) });
    return true;
  }

  emitEnvelope(obj, emit);

  /* Bước xử lý của server (tra dữ liệu, gọi mô hình…): chỉ cập nhật dòng trạng thái chờ,
     không thành chữ trong câu trả lời. */
  if (event === "progress" && streamProtocol !== "qonnex-agent") {
    const label = pickString(obj, ["label", "message"]);
    emit({ type: "progress", stage: pickString(obj, ["stage"]) || "progress", status: pickString(obj, ["status"]) || "started", ...(label ? { label } : {}) });
    return false;
  }

  if (streamProtocol === "qonnex-agent") {
    const result = normalizeQonnexAgentEvent(event, obj);
    for (const chunk of result.chunks) emit(chunk);
    if (result.handled) return result.done;
  }

  if (event === "done" || event === "end") return true;

  const delta = pickString(obj, ["delta", "chunk", "token"]);
  const full = pickString(obj, ["text", "content", "message", "reply", "answer"]);

  if (delta != null) {
    emit({ type: "delta", text: delta });
    return false;
  }
  if (full != null) {
    /* event "delta"/"token"/"chunk" mang trường text → mẩu nối thêm;
       mọi event khác (kể cả khi thiếu dòng event:) → câu trả lời trọn vẹn. */
    if (event === "delta" || event === "token" || event === "chunk") emit({ type: "delta", text: full });
    else emit({ type: "message", text: full, messageId: pickString(obj, ["messageId", "id"]) });
  }
  return false;
}

function normalizeSources(value: unknown): AnswerSource[] {
  if (!Array.isArray(value)) return [];
  const out: AnswerSource[] = [];
  for (const item of value) {
    if (typeof item === "string") {
      out.push({ label: item });
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const label = pickString(row, ["label", "name", "source", "title"]);
    if (!label) continue;
    const source: AnswerSource = { label };
    const at = pickString(row, ["at", "date", "asOf", "timestamp"]);
    if (at) source.at = at;
    const scope = pickString(row, ["scope", "range"]);
    if (scope) source.scope = scope;
    const note = pickString(row, ["note", "caveat", "disclaimer"]);
    if (note) source.note = note;
    const url = pickString(row, ["url", "href", "link"]);
    if (url && /^https?:\/\//i.test(url)) source.url = url;
    out.push(source);
  }
  return out;
}

const ACTION_TYPES = ["open_widget", "open_content", "open_panel", "link"];

function normalizeActions(value: unknown): AnswerAction[] {
  if (!Array.isArray(value)) return [];
  const out: AnswerAction[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const label = pickString(row, ["label", "title", "text"]);
    const target = pickString(row, ["target", "id", "href", "url"]);
    if (!label || !target) continue;
    const rawType = pickString(row, ["type", "kind"]) || "open_panel";
    const type = (ACTION_TYPES.includes(rawType) ? rawType : "open_panel") as AnswerAction["type"];
    const action: AnswerAction = { type, label, target };
    if (row.params && typeof row.params === "object") {
      action.params = row.params as Record<string, unknown>;
    }
    out.push(action);
    /* Spec: tối đa ba bước tiếp theo. Thừa thì cắt, đừng để server làm vỡ
       bố cục bong bóng. */
    if (out.length === 3) break;
  }
  return out;
}

/** Tệp server vừa tạo: chỉ id dạng file_… (xem trước qua /hub/files/:id), tên/loại cắt ngắn. */
export function normalizeFiles(value: unknown, limit = 4): Attachment[] {
  if (!Array.isArray(value)) return [];
  const out: Attachment[] = [];
  for (const f of value) {
    if (!f || typeof f !== "object") continue;
    const o = f as Record<string, unknown>;
    if (typeof o.id !== "string" || !/^file_\w{1,40}$/.test(o.id) || typeof o.type !== "string") continue;
    out.push({ id: o.id, name: typeof o.name === "string" ? o.name.slice(0, 120) : "tep", type: o.type.slice(0, 120), size: typeof o.size === "number" ? o.size : 0 });
    if (out.length >= limit) break;
  }
  return out;
}

const PROPOSAL_STATES = ["proposed", "awaiting_approval", "need_info", "sending", "done", "failed", "rejected", "cancelled"];

/** Đề xuất hành động từ server — chỉ giữ trường đã biết, chuỗi cắt ngắn, trạng thái lạ thì bỏ. */
export function normalizeProposals(value: unknown, limit = 4): ActionProposal[] {
  if (!Array.isArray(value)) return [];
  const out: ActionProposal[] = [];
  const s = (v: unknown, n = 300) => (typeof v === "string" ? v.slice(0, n) : "");
  for (const item of value.slice(0, limit)) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const id = s(r.id, 60);
    const state = s(r.state, 30);
    if (!id || !PROPOSAL_STATES.includes(state)) continue;
    out.push({
      id,
      type: s(r.type, 60),
      label: s(r.label, 120),
      title: s(r.title, 200) || s(r.label, 120),
      fields: (Array.isArray(r.fields) ? r.fields : [])
        .filter((f): f is unknown[] => Array.isArray(f) && f.length >= 2)
        .map((f) => [s(f[0], 60), s(f[1], 300)] as [string, string])
        .slice(0, 16),
      reason: s(r.reason, 500) || undefined,
      warnings: Array.isArray(r.warnings) ? r.warnings.map((w) => s(w, 240)).filter(Boolean).slice(0, 5) : undefined,
      target: s(r.target, 160) || undefined,
      needsApproval: r.needsApproval === true,
      selfApproved: r.selfApproved === true,
      approverLabel: s(r.approverLabel, 60) || null,
      policy: s(r.policy, 300) || null,
      priority: s(r.priority, 20) || undefined,
      state: state as ActionProposal["state"],
      requester: s(r.requester, 80) || undefined,
      deadline: typeof r.deadline === "number" ? r.deadline : null,
      record: s(r.record, 40) || null,
      demo: r.demo === true,
      checks: Array.isArray(r.checks)
        ? r.checks
            .filter((c): c is Record<string, unknown> => !!c && typeof c === "object")
            .map((c) => {
              const g = c.suggest && typeof c.suggest === "object" ? (c.suggest as Record<string, unknown>) : null;
              const suggest = g && s(g.from, 200) && s(g.to, 200) ? { from: s(g.from, 200), to: s(g.to, 200) } : undefined;
              return { label: s(c.label, 80), status: (c.status === "block" || c.status === "warn" ? c.status : "ok") as "ok" | "warn" | "block", detail: s(c.detail, 300), ...(suggest ? { suggest } : {}) };
            })
            .slice(0, 10)
        : undefined,
      blocked: r.blocked === true,
      overrideNote: s(r.overrideNote, 400) || undefined,
      error: s(r.error, 300) || undefined,
      history: Array.isArray(r.history)
        ? r.history
            .filter((h): h is Record<string, unknown> => !!h && typeof h === "object")
            .map((h) => ({ at: typeof h.at === "number" ? h.at : 0, by: s(h.by, 80), what: s(h.what, 300) }))
            .slice(-12)
        : undefined,
    });
  }
  return out;
}

function normalizeTrace(value: unknown): TraceStep[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((x): x is Record<string, unknown> => !!x && typeof x === "object" && typeof (x as { label?: unknown }).label === "string")
    .map((x) => ({ t: typeof x.t === "number" ? x.t : 0, label: String(x.label).slice(0, 200) }))
    .slice(0, 40);
}

function normalizeQuota(value: unknown): Quota | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const remaining = pickNumber(row, ["remaining", "left"]);
  const limit = pickNumber(row, ["limit", "max", "total"]);
  if (remaining == null || limit == null) return null;
  const quota: Quota = { remaining, limit };
  const resetAt = pickString(row, ["resetAt", "reset_at", "resetsAt"]);
  if (resetAt) quota.resetAt = resetAt;
  const period = pickString(row, ["period"]);
  if (period === "day" || period === "month") quota.period = period;
  return quota;
}

function pickString(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string") return value;
  }
  return undefined;
}

function pickNumber(source: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return undefined;
}

interface ErrorBody {
  message?: string;
  code?: string;
  /** Body đã parse — một 429 vẫn mang `quota`, và client phải dùng nó để khoá
   *  ô nhập. Vứt đi là người dùng cứ gõ tiếp rồi lại nhận 429. */
  raw?: Record<string, unknown>;
}

async function safeErrorBody(response: Response): Promise<ErrorBody> {
  try {
    const text = await response.text();
    if (!text) return {};
    try {
      const body = JSON.parse(text) as Record<string, unknown>;
      const inner = (body.error && typeof body.error === "object" ? body.error : body) as Record<string, unknown>;
      return {
        message: pickString(inner, ["message", "error", "detail"]),
        code: pickString(inner, ["code"]),
        raw: body,
      };
    } catch {
      return { message: text.slice(0, 200) };
    }
  } catch {
    return {};
  }
}

/** Lỗi từ backend cục bộ mang `status` (401, 429…) được đổi sang TransportError
 *  để widget xử lý y như lỗi HTTP: auth:error, khoá ô nhập khi hết hạn mức. */
async function runLocal<T>(fn: () => T | Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof TransportError) throw error;
    const e = error as { status?: unknown; code?: unknown; message?: unknown; name?: unknown };
    if (e && e.name === "AbortError") throw error;
    if (e && typeof e.status === "number") {
      throw new TransportError(String(e.message || "HTTP " + e.status), e.status, typeof e.code === "string" ? e.code : undefined);
    }
    throw error;
  }
}
